import { Collection } from 'firestorter';

import firebase from 'firebase/app';
import { firestorePaths } from '../utils/paths';
import { AssignmentRole, ConflictingRole } from '../utils/internalAssignments';
import BaseDocument from './BaseDocument';

const DISABLED_ASSIGNMENT_DATA = {
  current: false,
  isLiaison: false,
};

// Firestore rejects a batch over 500 writes.
const FIRESTORE_BATCH_WRITE_LIMIT = 500;

/**
 * Split per-coach pool writes into groups that each fit in one Firestore batch. A coach's AC
 * disable and POOL create have to commit together — that is what makes "never neither" hold — so
 * groups are only ever split between coaches, never inside one.
 * @param {Array<{coachId: string, conflictingAssignments: Array, isAlreadyPooled: boolean}>}
 * poolMemberships - The per-coach write plans, in the order they were requested.
 * @return {Array<Array<Object>>} The memberships grouped one group per batch commit.
 * @throws {Error} When one coach's own writes exceed the batch limit, which has no valid grouping.
 */
const groupByBatchWriteLimit = (poolMemberships) => {
  const groups = [];
  let currentGroup = null;

  poolMemberships.forEach((membership) => {
    const writeCount = membership.conflictingAssignments.length + (membership.isAlreadyPooled ? 0 : 1);

    /*
      A coach whose own writes cannot fit in one batch has no valid grouping — splitting them would
      break the atomicity that makes "never neither" hold. Say so here rather than letting Firestore
      reject an over-sized batch with nothing pointing at the cause.
    */
    if (writeCount > FIRESTORE_BATCH_WRITE_LIMIT) {
      throw new Error(`Coach ${membership.coachId} needs ${writeCount} pool writes, over the batch limit`);
    }

    if (!currentGroup || currentGroup.writeCount + writeCount > FIRESTORE_BATCH_WRITE_LIMIT) {
      currentGroup = { writeCount: 0, memberships: [] };
      groups.push(currentGroup);
    }

    currentGroup.writeCount += writeCount;
    currentGroup.memberships.push(membership);
  });

  return groups.map(({ memberships }) => memberships);
};

class InternalAssignment extends BaseDocument {
  get userId() {
    return this.data.userId;
  }

  get userName() {
    return this.data.userName;
  }

  get coach() {
    return this.data.coach;
  }

  get role() {
    return this.data.role;
  }

  get createdAt() {
    return this.data.createdAt.toDate();
  }

  get podId() {
    return this.data.podId;
  }

  get isLiaison() {
    return this.data.isLiaison;
  }

  get createdByUserName() {
    return this.data.createdByUserName;
  }

  disableAssignment = async () => (
    this.update(DISABLED_ASSIGNMENT_DATA)
  );

  static async getAllCurrentAssignments(userId = null, role = null, coachId = null, collectionOptions = {
    mode: 'auto',
    minimizeUpdates: false,
  }) {
    const internalAssignmentCollection = new Collection(firestorePaths.INTERNAL_ASSIGNMENT,
      {
        query: (ref) => {
          let query = ref.where('current', '==', true);
          if (userId) {
            query = query.where('userId', '==', userId);
          }
          if (role) {
            query = query.where('role', '==', role);
          }
          if (coachId) {
            query = query.where('coach', '==', coachId);
          }
          return query;
        },
        createDocument: (source, options) => new InternalAssignment(source, options),
        mode: collectionOptions.mode,
        minimizeUpdates: collectionOptions.minimizeUpdates,
      });
    await InternalAssignment.initCollection(internalAssignmentCollection);
    return internalAssignmentCollection;
  }

  static async getAllCurrentAssignmentsByUserId(userId) {
    return InternalAssignment.getAllCurrentAssignments(userId);
  }

  static async getAllCurrentAssignmentsByRole(role) {
    return InternalAssignment.getAllCurrentAssignments(null, role);
  }

  static async getCurrentAssignmentOfCoachByRole(coachId, role) {
    return InternalAssignment.getAllCurrentAssignments(null, role, coachId);
  }

  static async getCurrentAssignmentDocs(coachId, role) {
    const db = firebase.firestore();
    const assignments = await db
      .collection(firestorePaths.INTERNAL_ASSIGNMENT)
      .where('coach', '==', coachId)
      .where('role', '==', role)
      .where('current', '==', true)
      .get();

    return assignments.docs;
  }

  static async getConflictingAssignments(coachId, role) {
    const conflictingRole = ConflictingRole[role];
    if (!conflictingRole) {
      return [];
    }

    return InternalAssignment.getCurrentAssignmentDocs(coachId, conflictingRole);
  }

  static async disableConflictingAssignmentsInBatch(batch, coachId, role) {
    const conflictingAssignments = await InternalAssignment.getConflictingAssignments(coachId, role);

    conflictingAssignments.forEach((assignment) => batch.update(assignment.ref, DISABLED_ASSIGNMENT_DATA));
  }

  static async addInternalAssignment(coachId, userId, firstName, role, createdBy, isLiaison = false) {
    const db = firebase.firestore();
    const batch = db.batch();

    // Find if there's an existing internal assignment for this coach
    const [existingAssignmentQuery, existingLiaisonQuery] = await Promise.all([
      db
        .collection(firestorePaths.INTERNAL_ASSIGNMENT)
        .where('coach', '==', coachId)
        .where('userId', '==', userId)
        .where('role', '==', role)
        .where('current', '==', true)
        .get(),
      isLiaison
        ? db
          .collection(firestorePaths.INTERNAL_ASSIGNMENT)
          .where('coach', '==', coachId)
          .where('isLiaison', '==', true)
          .where('role', '==', role)
          .where('current', '==', true)
          .get()
        : null,
    ]);

    await InternalAssignment.disableConflictingAssignmentsInBatch(batch, coachId, role);

    // Handle liaison user update if needed
    if (isLiaison) {
      if (!existingLiaisonQuery.empty) {
        throw new Error('A liaison is already assigned to this coach');
      }

      const userRef = db.collection(firestorePaths.USER).doc(userId);
      batch.update(userRef, { isLiaison: true });
    }

    const assignmentData = {
      coach: coachId,
      userId,
      role,
      createdBy,
      current: true,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      userName: firstName,
      isLiaison,
    };

    if (!existingAssignmentQuery.empty) {
      // Update existing assignment
      const existingAssignment = existingAssignmentQuery.docs[0];
      batch.update(existingAssignment.ref, assignmentData);
    } else {
      // Create new assignment only if one doesn't exist
      const newAssignmentRef = db.collection(firestorePaths.INTERNAL_ASSIGNMENT).doc();
      batch.set(newAssignmentRef, {
        ...assignmentData,
        createdAt: new Date(),
      });
    }

    await batch.commit();
  }

  /**
   * Move coaches into the pool, disabling each one's current `AC` rows in the same batch so a coach
   * is never left with neither an AC nor pool membership.
   *
   * A coach who is already pooled keeps their existing `POOL` row, but their `AC` rows are still
   * disabled — that is a real change, so it is reported separately rather than folded into "already
   * pooled, nothing happened". Only a coach who was already pooled *and* had no `AC` row is absent
   * from both lists.
   *
   * The AC↔pool exclusion is a rule of this method, not an invariant of the collection: any writer
   * that creates an `AC` or `POOL` row without coming through here (or through
   * `addInternalAssignment`) bypasses it.
   *
   * Callers must exclude pod-enabled coaches. That rule is a policy over `coachConfig` rather than an
   * invariant of this collection, so it is not enforced here — the AC↔pod link is a field on the
   * assignment row, and pooling a pod coach breaks their rebalance. `useMoveToPool` is what checks it.
   * @param {Array<string>} coachIds - The coaches to pool, none of them pod-enabled.
   * @param {string} createdBy - The user id of the admin performing the change.
   * @param {string} createdByUserName - The display name of that admin, denormalized onto the row.
   * @return {Promise<{pooledCoachIds: Array<string>, unassignedFromAcCoachIds: Array<string>}>} The
   * coaches a `POOL` row was created for, and the coaches whose `AC` rows were disabled. A coach can
   * appear in both.
   * @throws {Error} When a batch fails to commit. Groups that committed before the failure are
   * durable, so the same shape is attached to the error as `committedPoolChanges` for the caller to
   * report.
   */
  static async addCoachesToPool(coachIds, createdBy, createdByUserName) {
    const db = firebase.firestore();

    const poolMemberships = await Promise.all(coachIds.map(async (coachId) => {
      const [existingPoolAssignments, conflictingAssignments] = await Promise.all([
        InternalAssignment.getCurrentAssignmentDocs(coachId, AssignmentRole.POOL),
        InternalAssignment.getConflictingAssignments(coachId, AssignmentRole.POOL),
      ]);

      return {
        coachId,
        conflictingAssignments,
        isAlreadyPooled: !!existingPoolAssignments.length,
      };
    }));

    const groups = groupByBatchWriteLimit(poolMemberships);
    const committed = { pooledCoachIds: [], unassignedFromAcCoachIds: [] };

    // Committed one group after another so a rejected batch stops the run instead of racing the rest.
    for (let index = 0; index < groups.length; index += 1) {
      const batch = db.batch();
      const pooledInGroup = [];
      const unassignedInGroup = [];

      groups[index].forEach(({ coachId, conflictingAssignments, isAlreadyPooled }) => {
        if (conflictingAssignments.length) {
          conflictingAssignments.forEach((assignment) => batch.update(assignment.ref, DISABLED_ASSIGNMENT_DATA));
          unassignedInGroup.push(coachId);
        }

        if (!isAlreadyPooled) {
          batch.set(db.collection(firestorePaths.INTERNAL_ASSIGNMENT).doc(), {
            coach: coachId,
            role: AssignmentRole.POOL,
            current: true,
            createdBy,
            createdByUserName,
            createdAt: new Date(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          });
          pooledInGroup.push(coachId);
        }
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
      } catch (error) {
        // Everything committed so far is durable, so hand it back instead of losing it in the reject.
        throw Object.assign(error, { committedPoolChanges: committed });
      }

      committed.pooledCoachIds.push(...pooledInGroup);
      committed.unassignedFromAcCoachIds.push(...unassignedInGroup);
    }

    return committed;
  }

  static async getAssignmentsByPodId(podId) {
    const assignmentCollection = new Collection(firestorePaths.INTERNAL_ASSIGNMENT, {
      createDocument: (source, options) => new InternalAssignment(source, options),
      query: (query) => query
        .where('podId', '==', podId)
        .where('role', '==', 'AC')
      ,
    });
    await InternalAssignment.initCollection(assignmentCollection);

    return assignmentCollection.hasDocs ? assignmentCollection.docs : null;
  }

  static async getAssignmentsByPodIdAnsAC(podId, acId) {
    const assignmentCollection = new Collection(firestorePaths.INTERNAL_ASSIGNMENT, {
      createDocument: (source, options) => new InternalAssignment(source, options),
      query: (query) => query
        .where('podId', '==', podId)
        .where('userId', '==', acId)
        .where('role', '==', 'AC')
        .limit(1)
      ,
    });
    await InternalAssignment.initCollection(assignmentCollection);

    return assignmentCollection.hasDocs ? assignmentCollection.docs[0] : null;
  }
}

export default InternalAssignment;
// Exported for tests: the batch-splitting rule is the part of the pool write that can silently
// produce an uncommittable batch, and it is pure logic over the write plans.
export { groupByBatchWriteLimit };
